import { useMemo } from 'react';
import {
  Alert,
  type AlertColor,
  alpha,
  Box,
  Button,
  ButtonGroup,
  Typography,
} from '@mui/material';
import { TIncident } from '../../../../../utility/types/dataProvider';
import { useWidgetContext } from 'components/Dashboard/Widget';
import Incident from './Incident';

/**
 * Get the overall status of the incidents.
 * Ref: https://kb.status.io/developers/status-codes/
 * @param statuses array of incidents.
 * @returns { status: string, variant: AlertColor }
 */
export const getOverallStatus = (
  statuses: TIncident[],
): { status: string; variant: AlertColor } => {
  // If no statuses, return all systems operational.
  if (!statuses?.length) {
    return { status: 'All Systems Operational', variant: 'success' };
  }

  // Get all the status codes from the incidents.
  const allStatusCodes = statuses.map((status: TIncident) => Number(status?.status));

  // Get the max status code from statusCodeResults.
  const maxStatusCode = Math.max(...allStatusCodes);

  // Get the most occurring status code from allStatusCodes
  const modeStatusCode = allStatusCodes.reduce(
    (a, b, i, arr) =>
      arr.filter((v) => v === a).length >= arr.filter((v) => v === b).length ? a : b,
    allStatusCodes[0],
  );

  // Use the higher severity between max and mode
  const weightedStatusCode = Math.max(maxStatusCode, modeStatusCode);

  // Check if the max status code is greater than 300.
  const finalStatusCode = maxStatusCode >= 300 ? weightedStatusCode : modeStatusCode;

  // Return the overall status based on the max status code.
  switch (finalStatusCode) {
    case 600:
      return { status: 'Security Issue', variant: 'error' };
    case 500:
      return { status: 'Service Disruption', variant: 'error' };
    case 400:
      return { status: 'Partial Service Disruption', variant: 'warning' };
    case 300:
      return { status: 'Degraded Performance', variant: 'warning' };
    case 200:
      return { status: 'Planned Maintenance', variant: 'info' };
    default:
      return { status: 'All Systems Operational', variant: 'success' };
  }
};

// Renders the status.io service advisories.
const IncidentStatus = () => {
  const { data } = useWidgetContext();

  // Get all the statuses/incidents, then sort in descending and alphabetic order.
  const { statuses, maintenance } = useMemo(() => {
    const results = data?.results ?? [];

    // If no results, return empty arrays.
    if (!results.length) {
      return {
        statuses: [],
        maintenance: [],
      };
    }

    const statuses = results.filter((result: TIncident) => result?.type === 'incident');

    // Sort by status and name properties.
    const sortedStatuses = statuses.sort((a: TIncident, b: TIncident) => {
      const numStatusA = Number(a.status);
      const numStatusB = Number(b.status);

      // First sort by status in descending order.
      if (numStatusB !== numStatusA) {
        return numStatusB - numStatusA;
      }

      // Then sort by name alphabetically.
      return (a.name ?? '').localeCompare(b.name ?? '');
    });

    const sortedMaintenance = results
      .filter((result: TIncident) => result?.type === 'maintenance')
      ?.map((result: TIncident) => ({
        ...result,
        // Manually set to 200 for planned maintenance, since there are missing status codes for maintenance items.
        status: '200',
      }))
      ?.sort((a: TIncident, b: TIncident) => {
        // First sort by status in descending order.
        if (a.status !== b.status) {
          return Number(b.status) - Number(a.status);
        }

        // Then sort by name alphabetically.
        return (a.name ?? '').localeCompare(b.name ?? '');
      });

    return {
      statuses: sortedStatuses,
      maintenance: sortedMaintenance,
    };
  }, [data]);

  // Check all results from status.io and return the overall status.
  const { status, variant } = useMemo(
    () => getOverallStatus([...statuses, ...maintenance]),
    [statuses, maintenance],
  );

  return (
    <Box
      id="service-advisories-incident-statuses"
      data-testid="help-service-updates-section"
      sx={{ my: 2 }}
    >
      {/* Overall Status */}
      <Alert
        severity={variant}
        icon={false}
        sx={(theme) => {
          const isLightTheme = theme.palette.mode === 'light';
          const bgBlue = isLightTheme
            ? alpha(theme.blox.colors.logoblue, 0.1)
            : theme.blox.colors.darkModeBlue;
          return {
            mb: 2,
            ...(variant === 'info' && { backgroundColor: bgBlue }),
            '& .MuiAlert-message': {
              color: isLightTheme
                ? theme.blox.colors.offblack
                : theme.blox.colors.offwhite,
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            },
          };
        }}
      >
        <Typography
          variant="subtitle2"
          component="span"
        >
          {status ?? 'All Systems Operational'}
        </Typography>

        <ButtonGroup
          variant="contained"
          aria-label="Basic button group"
          sx={(theme) => {
            const isLightTheme = theme.palette.mode === 'light';
            const offBlack = theme.blox.colors.offblack;
            const offWhite = theme.blox.colors.offwhite;
            const blue = theme.blox.colors.blue;

            return {
              '.MuiButton-root': {
                backgroundColor: isLightTheme ? offWhite : offBlack,
                borderColor: isLightTheme ? offBlack : offWhite,
                color: isLightTheme ? offBlack : offWhite,
                '&:hover': {
                  backgroundColor: isLightTheme ? blue : offWhite,
                  color: isLightTheme ? offWhite : offBlack,
                },
              },
            };
          }}
        >
          <Button
            data-testid="help-status-link"
            href="https://townnews.status.io/"
            target="_blank"
          >
            More Info
          </Button>
          <Button
            data-testid="help-subscribe-link"
            href="https://townnews.status.io/"
            target="_blank"
          >
            Subscribe
          </Button>
        </ButtonGroup>
      </Alert>

      {/* All Incidents. */}
      <Box
        sx={{
          px: 2,
        }}
      >
        {!!statuses?.length &&
          statuses?.map((incident: TIncident) => (
            <Incident
              key={incident?.id}
              incident={incident}
            />
          ))}
      </Box>

      {/* All Maintenance. */}
      <Box
        sx={{
          px: 2,
        }}
      >
        {!!maintenance?.length &&
          maintenance?.map((incident: TIncident) => (
            <Incident
              key={incident?.id}
              incident={incident}
            />
          ))}
      </Box>
    </Box>
  );
};

export default IncidentStatus;
